import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, Camera, CheckCircle2, Clock, IdCard, Image as ImageIcon,
  Loader2, RefreshCw, ScanFace, ShieldCheck, Trash2,
} from 'lucide-react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { usePortalDestination, usePortalDraft } from '../PortalDestinationContext';

const identityApi = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: true,
});

const blank = {
  id_type: 'ghana_card', full_name: '', date_of_birth: '', id_number: '', id_expiry: '',
  residential_address: '', town: '', phone: '', phone_confirmed: false,
};

const STATE_COPY = {
  pending_review: ['Under review', 'Your pharmacy is checking your ID. You can keep shopping meanwhile.'],
  verified: ['Verified', 'Your identity is confirmed with this pharmacy.'],
  rejected: ['Not accepted', 'Please read the note below and send a fresh photo.'],
  photo_requested: ['Better photo needed', 'Please read the note below and take another photo.'],
};

const STEPS = ['id_type', 'id_capture', 'selfie', 'details', 'review'];
const STEP_TITLES = {
  id_type: 'ID type', id_capture: 'ID capture', selfie: 'Selfie check', details: 'Details', review: 'Review',
};

const maskId = (value) => {
  if (!value) return '';
  const s = String(value);
  return s.length <= 4 ? '•'.repeat(s.length) : `${'•'.repeat(s.length - 2)}${s.slice(-2)}`;
};

const detailFields = (spec) => [
  ['full_name', 'Full name as on the ID', 'text'],
  ['date_of_birth', 'Date of birth', 'date'],
  ['id_number', `${spec?.label || 'ID'} number`, 'text'],
  ['id_expiry', `${spec?.label || 'ID'} expiry date${spec?.expiry_required ? '' : ' (if shown)'}`, 'date'],
  ['residential_address', 'Where you live', 'text'],
  ['town', 'Town', 'text'],
  ['phone', 'Phone number', 'tel'],
];

export default function PortalVerifyIdentityPage() {
  const { pharmacy, href } = usePortalDestination();
  const navigate = useNavigate();
  const [types, setTypes] = useState([]);
  const [typesError, setTypesError] = useState('');
  const [record, setRecord] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [form, setForm] = useState(blank);
  const [stepIdx, setStepIdx] = useState(0);
  const [idFile, setIdFile] = useState(null);
  const [idPreviewUrl, setIdPreviewUrl] = useState('');
  const [reading, setReading] = useState(false);
  const [readNote, setReadNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [extracted, setExtracted] = useState(null);
  const [challenge, setChallenge] = useState(null);
  const [shots, setShots] = useState([]);
  const [shotPreviews, setShotPreviews] = useState([]);
  const idCameraRef = useRef(null);
  const idFileRef = useRef(null);
  const shotRef = useRef(null);
  const step = STEPS[stepIdx];

  const load = useCallback(() => {
    if (!pharmacy?.tenant_id) return;
    setTypesError('');
    identityApi.get('/identity/options').then(r => setTypes(r.data.id_types || []))
      .catch(() => setTypesError('Could not load ID types — check your connection and try again.'));
    identityApi.get('/identity/me', { params: { pharmacy_id: pharmacy.tenant_id } })
      .then(r => { setRecord(r.data.verification); setLoaded(true); })
      .catch(() => setLoaded(true));
  }, [pharmacy?.tenant_id]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => () => { if (idPreviewUrl) URL.revokeObjectURL(idPreviewUrl); }, [idPreviewUrl]);
  useEffect(() => {
    const urls = shots.map(s => URL.createObjectURL(s.file));
    setShotPreviews(urls);
    return () => urls.forEach(u => URL.revokeObjectURL(u));
  }, [shots]);

  const spec = types.find(t => t.value === form.id_type);
  const nextShot = challenge ? challenge.poses[shots.length] : null;
  const requiredShots = challenge?.shots || 3;
  usePortalDraft(!!idFile || shots.length > 0, busy,
    [idFile && 'your ID document photo', shots.length > 0 && `${shots.length} selfie photo${shots.length > 1 ? 's' : ''}`].filter(Boolean).join(' and '));

  const captureId = async (file) => {
    if (!file) return;
    setIdFile(file);
    setIdPreviewUrl(URL.createObjectURL(file));
    setExtracted(null); setReadNote(''); setReading(true);
    const body = new FormData();
    body.append('pharmacy_id', pharmacy.tenant_id);
    body.append('id_type', form.id_type);
    body.append('id_image', file);
    try {
      const { data } = await identityApi.post('/identity/extract', body);
      if (data.extracted) {
        setExtracted(data.extracted);
        setForm(f => ({
          ...f,
          full_name: data.extracted.full_name || f.full_name,
          id_number: data.extracted.id_number || f.id_number,
          date_of_birth: data.extracted.date_of_birth || f.date_of_birth,
          id_expiry: data.extracted.id_expiry || f.id_expiry,
        }));
        setReadNote('We read your ID — check every line and correct anything that is wrong.');
      } else {
        setReadNote(data.reason || 'We could not read the photo — type your details instead.');
      }
    } catch {
      setReadNote('We could not read the photo — type your details instead.');
    } finally { setReading(false); }
  };
  const retakeId = () => { setIdFile(null); setIdPreviewUrl(''); setExtracted(null); setReadNote(''); };

  const startLiveness = async () => {
    try {
      const { data } = await identityApi.post('/identity/liveness/challenge', null,
        { params: { pharmacy_id: pharmacy.tenant_id } });
      setChallenge(data);
      setShots([]);
      toast.success(`Follow the ${data.shots || 3} prompts — it takes about ten seconds`);
    } catch { toast.error('Could not start the selfie check'); }
  };
  const captureShot = (file) => { if (file && nextShot) setShots(prev => [...prev, { ...nextShot, file }]); };
  const retakeLastShot = () => setShots(prev => prev.slice(0, -1));

  const submit = async () => {
    if (!idFile) return toast.error('A photo of your ID is required');
    if (shots.length < requiredShots) return toast.error(`Finish the ${requiredShots} selfie prompts so we know it is really you`);
    setBusy(true);
    const body = new FormData();
    body.append('pharmacy_id', pharmacy.tenant_id);
    Object.entries(form).forEach(([k, v]) => body.append(k, typeof v === 'boolean' ? String(v) : v));
    body.append('extracted_json', extracted ? JSON.stringify(extracted) : '');
    body.append('id_image', idFile);
    body.append('challenge_id', challenge?.challenge_id || '');
    body.append('selfie', shots[0].file);
    if (shots[1]) body.append('selfie_2', shots[1].file);
    if (shots[2]) body.append('selfie_3', shots[2].file);
    try {
      const { data } = await identityApi.post('/identity/verifications', body);
      setRecord(data.verification);
      toast.success('Sent for review — we will tell you as soon as it is checked');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not send your verification');
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm('Delete the ID details and photos you sent this pharmacy?')) return;
    try {
      await identityApi.delete('/identity/me', { params: { pharmacy_id: pharmacy.tenant_id } });
      setRecord(null); setForm(blank); setIdFile(null); setIdPreviewUrl('');
      setExtracted(null); setChallenge(null); setShots([]); setReadNote(''); setStepIdx(0);
      toast.success('Deleted. You can verify again any time.');
    } catch { toast.error('Could not delete your verification'); }
  };

  if (!pharmacy) return <p className="p-4 text-sm text-slate-600" data-testid="verify-no-pharmacy">Choose a pharmacy first.</p>;
  if (!loaded) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>;

  const live = record && ['pending_review', 'verified'].includes(record.status);

  const statusCard = record && (
    <section className={`rounded-xl border p-4 ${record.status === 'verified' ? 'border-emerald-300 bg-emerald-50' : record.status === 'pending_review' ? 'border-amber-300 bg-amber-50' : 'border-red-300 bg-red-50'}`}
      data-testid="verify-status-card">
      <p className="font-semibold text-slate-900 flex items-center gap-2" data-testid="verify-status">
        {record.status === 'verified' ? <CheckCircle2 className="w-4 h-4 text-emerald-700" /> : <Clock className="w-4 h-4" />}
        {(STATE_COPY[record.status] || [record.status])[0]}
      </p>
      <p className="text-sm text-slate-700 mt-1">{(STATE_COPY[record.status] || ['', ''])[1]}</p>
      {record.decision_reason && (
        <p className="text-sm text-slate-900 bg-white/70 border rounded-lg px-3 py-2 mt-2" data-testid="verify-reason">
          {record.decision_reason}
        </p>
      )}
      <div className="text-xs text-slate-600 mt-2">
        {record.id_type_label} · sent {record.submitted_at ? new Date(record.submitted_at).toLocaleDateString() : '—'}
      </div>
      {record.id_number && (
        <div className="text-xs text-slate-600" data-testid="verify-status-id-masked">
          {record.id_type_label} number: {maskId(record.id_number)}
        </div>
      )}
      <Button type="button" variant="outline" size="sm" className="mt-3 gap-1.5" onClick={remove} data-testid="verify-delete">
        <Trash2 className="w-3.5 h-3.5" /> Delete my ID details
      </Button>
    </section>
  );

  if (live) {
    return (
      <div className="max-w-xl mx-auto px-4 py-5 space-y-5" data-testid="portal-verify-identity">
        <header>
          <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-emerald-600" /> Verify your identity
          </h1>
        </header>
        {statusCard}
      </div>
    );
  }

  const canContinue = {
    id_type: !!form.id_type && types.length > 0,
    id_capture: !!idFile && !reading,
    selfie: shots.length >= requiredShots,
    details: !!form.full_name.trim() && !!form.id_number.trim(),
    review: true,
  }[step];
  const primaryLabel = step === 'review' ? (busy ? 'Sending…' : 'Send for review') : 'Continue';
  const goBack = () => { if (stepIdx === 0) navigate(href('profile')); else setStepIdx(i => i - 1); };
  const goNext = () => setStepIdx(i => Math.min(STEPS.length - 1, i + 1));

  return (
    <div className="max-w-xl mx-auto px-4 py-4 space-y-4" data-testid="portal-verify-identity">
      {statusCard}
      <div className="portal-flow" data-testid="portal-verify-flow">
        <div className="portal-flow-header">
          <button type="button" onClick={goBack} aria-label="Back" data-testid="verify-flow-back"><ArrowLeft size={20} /></button>
          <h1>{STEP_TITLES[step]}</h1>
          <span aria-hidden="true" />
        </div>
        <div className="portal-flow-steps" aria-hidden="true">
          {STEPS.map((s, i) => <span key={s} className={i <= stepIdx ? 'is-done' : ''} />)}
        </div>

        <div className="portal-flow-body">
          {step === 'id_type' && (
            <div data-testid="verify-step-id-type">
              <p className="text-sm text-slate-500 mb-3">Pick the document you'll photograph next.</p>
              {types.map(t => (
                <button type="button" key={t.value}
                  className={`portal-flow-choice${form.id_type === t.value ? ' is-selected' : ''}`}
                  onClick={() => setForm(f => ({ ...f, id_type: t.value }))} data-testid={`verify-id-type-${t.value}`}>
                  <IdCard size={22} />
                  <span className="flex-1 text-left">
                    <span className="block">{t.label}</span>
                    <span className="block text-xs text-slate-500 font-normal">e.g. {t.hint}</span>
                  </span>
                  {form.id_type === t.value && <CheckCircle2 size={18} className="text-emerald-600" />}
                </button>
              ))}
              {!types.length && !typesError && <p className="text-sm text-slate-500" data-testid="verify-types-loading">Loading ID types…</p>}
              {typesError && (
                <div className="verify-reading-status justify-between" data-testid="verify-types-error">
                  <span>{typesError}</span>
                  <button type="button" className="verify-retry-btn" onClick={load} data-testid="verify-types-retry">
                    <RefreshCw size={13} /> Retry
                  </button>
                </div>
              )}
            </div>
          )}

          {step === 'id_capture' && (
            <div data-testid="verify-step-id-capture">
              <p className="text-sm text-slate-500 mb-3">Photo of your {spec?.label || 'ID'}. We read it for you — you'll confirm next.</p>
              {!idFile && (
                <>
                  <ul className="verify-tips" data-testid="verify-capture-tips">
                    <li>Lay it flat on a plain, dark surface</li>
                    <li>Fill the frame — no glare or shadows</li>
                    <li>All four corners and text must be visible</li>
                  </ul>
                  <button type="button" className="portal-flow-choice" onClick={() => idCameraRef.current?.click()} data-testid="verify-id-camera">
                    <Camera size={22} /> Take photo
                  </button>
                  <button type="button" className="portal-flow-choice" onClick={() => idFileRef.current?.click()} data-testid="verify-id-gallery">
                    <ImageIcon size={22} /> Choose from gallery
                  </button>
                  <input ref={idCameraRef} type="file" accept="image/*" capture="environment" className="absolute opacity-0 w-0 h-0 overflow-hidden"
                    onChange={e => { captureId(e.target.files?.[0]); e.target.value = ''; }} data-testid="verify-id-camera-input" />
                  <input ref={idFileRef} type="file" accept="image/*" className="absolute opacity-0 w-0 h-0 overflow-hidden"
                    onChange={e => { captureId(e.target.files?.[0]); e.target.value = ''; }} data-testid="verify-id-file-input" />
                </>
              )}
              {idFile && (
                <div className="space-y-3" data-testid="verify-id-preview">
                  <img src={idPreviewUrl} alt="Captured ID" className="portal-flow-preview-img" />
                  <Button variant="outline" className="w-full" onClick={retakeId} data-testid="verify-id-retake">Retake photo</Button>
                  {reading && (
                    <p className="verify-reading-status" data-testid="verify-reading-status">
                      <Loader2 className="w-4 h-4 animate-spin" /> Reading your ID…
                    </p>
                  )}
                  {!reading && extracted && (
                    <div className="verify-extract-card" data-testid="verify-extract-confirm">
                      <p className="font-semibold text-emerald-800 text-sm flex items-center gap-1.5"><CheckCircle2 size={15} /> We read your ID</p>
                      <dl className="verify-extract-grid">
                        <dt>Name</dt><dd>{extracted.full_name || '—'}</dd>
                        <dt>ID number</dt><dd>{extracted.id_number || '—'}</dd>
                        <dt>Date of birth</dt><dd>{extracted.date_of_birth || '—'}</dd>
                        <dt>Expiry</dt><dd>{extracted.id_expiry || '—'}</dd>
                      </dl>
                      <p className="text-xs text-slate-500 mt-1">Confirm or correct these in the next step.</p>
                    </div>
                  )}
                  {!reading && !extracted && readNote && <p className="text-xs text-slate-700" data-testid="verify-read-note">{readNote}</p>}
                </div>
              )}
            </div>
          )}

          {step === 'selfie' && (
            <div data-testid="verify-step-selfie">
              <p className="text-sm text-slate-500 mb-3">
                We need brief camera access for {requiredShots} quick photos — proving a real person is here.
                A photo of a photo will not pass, and nothing leaves this pharmacy's review queue.
              </p>
              {(!challenge || nextShot) && (
                <div className="verify-face-guide" aria-hidden="true" data-testid="verify-face-guide">
                  <div className="verify-face-guide-oval"><ScanFace size={38} strokeWidth={1.2} /></div>
                  <p className="verify-face-guide-caption">Center your face inside the oval, in good light</p>
                </div>
              )}
              {!challenge ? (
                <button type="button" className="portal-flow-choice" onClick={startLiveness} data-testid="verify-start-liveness">
                  <ShieldCheck size={22} /> Start the selfie check
                </button>
              ) : (
                <div className="space-y-3" data-testid="verify-liveness-block">
                  <div className="verify-selfie-slots">
                    {challenge.poses.map((p, i) => (
                      <div key={p.key + i} className={`verify-selfie-slot ${i < shots.length ? 'is-done' : i === shots.length ? 'is-active' : ''}`}
                        data-testid={`verify-shot-slot-${i + 1}`}>
                        {shotPreviews[i] ? <img src={shotPreviews[i]} alt="" /> : <span>{i + 1}</span>}
                      </div>
                    ))}
                  </div>
                  {nextShot ? (
                    <>
                      <p className="font-semibold text-slate-900 text-sm" data-testid="verify-pose-prompt">{nextShot.label}</p>
                      <button type="button" className="portal-flow-choice" onClick={() => shotRef.current?.click()} data-testid="verify-shot-camera">
                        <Camera size={22} /> Take this photo
                      </button>
                      <input ref={shotRef} type="file" accept="image/*" capture="user" className="absolute opacity-0 w-0 h-0 overflow-hidden"
                        onChange={e => { captureShot(e.target.files?.[0]); e.target.value = ''; }} data-testid="verify-shot-input" />
                    </>
                  ) : (
                    <p className="text-sm text-emerald-700 font-medium" data-testid="verify-shots-done">All {requiredShots} photos captured.</p>
                  )}
                  <div className="flex gap-4 text-xs pt-1">
                    {shots.length > 0 && (
                      <button type="button" className="underline text-slate-500" onClick={retakeLastShot} data-testid="verify-retake-last">Retake last</button>
                    )}
                    <button type="button" className="underline text-slate-500" onClick={startLiveness} data-testid="verify-restart-liveness">Start over</button>
                  </div>
                </div>
              )}
            </div>
          )}

          {step === 'details' && (
            <div className="space-y-4" data-testid="verify-step-details">
              <p className="text-sm text-slate-500">Check every line — correct anything that's wrong.</p>
              {detailFields(spec).map(([key, label, type]) => (
                <div className="space-y-1.5" key={key}>
                  <Label className="text-xs flex items-center gap-1.5">
                    {label}
                    {extracted?.[key] && <span className="verify-from-id-tag">From your ID</span>}
                  </Label>
                  <Input type={type} className="h-12" value={form[key]} placeholder={key === 'id_number' ? spec?.hint : ''}
                    onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                    data-testid={`verify-field-${key.replace(/_/g, '-')}`} />
                </div>
              ))}
              <label className="flex items-start gap-2 text-sm text-slate-700 pt-1">
                <input type="checkbox" className="mt-1" checked={form.phone_confirmed}
                  onChange={e => setForm(f => ({ ...f, phone_confirmed: e.target.checked }))}
                  data-testid="verify-phone-confirmed" />
                This phone number is mine and reachable today.
              </label>
            </div>
          )}

          {step === 'review' && (
            <div data-testid="verify-step-review">
              <p className="text-sm text-slate-500 mb-3">Make sure everything is correct — your pharmacy sees exactly this.</p>
              <dl className="space-y-0">
                <div className="portal-flow-review-row"><dt>ID type</dt><dd>{spec?.label || '—'}</dd></div>
                {detailFields(spec).map(([key, label]) => (
                  <div className="portal-flow-review-row" key={key}>
                    <dt>{label}</dt><dd data-testid={`verify-review-${key.replace(/_/g, '-')}`}>{form[key] || '—'}</dd>
                  </div>
                ))}
                <div className="portal-flow-review-row"><dt>Phone confirmed</dt><dd>{form.phone_confirmed ? 'Yes' : 'No'}</dd></div>
                <div className="portal-flow-review-row"><dt>Selfie photos</dt><dd>{shots.length} captured</dd></div>
              </dl>
              {idPreviewUrl && <img src={idPreviewUrl} alt="Your ID" className="portal-flow-preview-img mt-3" data-testid="verify-review-id-photo" />}
              <p className="text-xs text-slate-500 mt-3">
                Your ID photo and details are encrypted, seen only by staff of pharmacies you connected to, and deletable whenever you ask.
              </p>
            </div>
          )}
        </div>

        <div className="portal-flow-sticky-action">
          <Button className="w-full h-12" disabled={!canContinue || busy}
            onClick={step === 'review' ? submit : goNext} data-testid="verify-flow-primary">
            {step === 'review' && busy && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            {primaryLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
